import type {ReactRef} from 'src/types/react';

import * as React from 'react';


type DragContext = {
  pointerId: number;
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
  deltaX: number;
  deltaY: number;
  target: EventTarget;
  timeStamp: number;
};

export function usePointerDrag({
  onDown,
  onMove,
  onUp,
  onCancel,
  onLost,
  onDone,
  draggingClassName,
  validateDownEvent,
}: {
  onDown?: (arg1: DragContext) => unknown;
  onMove?: (arg1: DragContext) => unknown;
  onUp?: (arg1: DragContext) => unknown;
  onCancel?: (arg1: DragContext) => unknown;
  onLost?: (arg1: DragContext) => unknown;
  onDone?: (arg1: DragContext) => unknown;
  draggingClassName?: string;
  validateDownEvent?: (arg1: React.SyntheticEvent) => unknown;
}): [
  {
    onPointerDown: (event: React.PointerEvent) => void;
    onPointerMove: (event: React.PointerEvent) => void;
    onPointerUp: (event: React.PointerEvent) => void;
    onPointerCancel: (event: React.PointerEvent) => void;
    onLostPointerCapture: (event: React.PointerEvent) => void;
    onContextMenu: (event: React.SyntheticEvent) => void;
  },
  ReactRef<DragContext>,
] {
  const definedOnCancel = onCancel ?? onUp;
  const definedOnLost = onLost ?? onUp;

  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
  const dragContextRef: ReactRef<DragContext> = React.useRef<
    DragContext | null | undefined
  >();

  const handlePointerMove = (
    event: React.PointerEvent,
    handler: undefined | ((arg1: DragContext) => unknown),
  ) => {
    const dragContext = dragContextRef.current;
    if (!dragContext || event.pointerId !== dragContext.pointerId) {
      return;
    }

    dragContext.deltaX = event.clientX - dragContext.currentX;
    dragContext.deltaY = event.clientY - dragContext.currentY;

    dragContext.currentX = event.clientX;
    dragContext.currentY = event.clientY;

    handler && handler(dragContext);
  };

  const handlePointerDone = (
    event: React.PointerEvent,
    handler: undefined | ((arg1: DragContext) => unknown),
  ) => {
    handlePointerMove(event, handler);
    handlePointerFinally(event);
  };

  const handlePointerFinally = (_event: React.SyntheticEvent) => {
    const {body} = document;
    body && draggingClassName && body.classList.remove(draggingClassName);
    onDone && dragContextRef.current && onDone(dragContextRef.current);
    dragContextRef.current = null;
  };

  return [
    {
      onPointerDown(event: React.PointerEvent) {
        if (
          !event.isPrimary ||
          (validateDownEvent && !validateDownEvent(event))
        ) {
          return;
        }

        event.preventDefault();

        const {body} = document;
        body && draggingClassName && body.classList.add(draggingClassName);

        event.currentTarget.setPointerCapture(event.pointerId);

        const dragContext = (dragContextRef.current = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          currentX: event.clientX,
          currentY: event.clientY,
          timeStamp: event.timeStamp,
          target: event.target,
          deltaX: 0,
          deltaY: 0,
        });

        onDown && onDown(dragContext);
      },

      onPointerMove(event: React.PointerEvent) {
        handlePointerMove(event, onMove);
      },

      onPointerUp(event: React.PointerEvent) {
        event.preventDefault();

        // NOTE (kyle): if the pointerup event fires within 200ms, we call it a
        // click.
        const dragContext = dragContextRef.current;
        if (
          dragContext &&
          event.pointerId === dragContext.pointerId &&
          event.timeStamp - dragContext.timeStamp < 200
        ) {
          const {target} = dragContext;
          target instanceof HTMLElement && target.click();
        }

        handlePointerDone(event, onUp);
      },

      onPointerCancel(event: React.PointerEvent) {
        handlePointerDone(event, definedOnCancel);
      },

      onLostPointerCapture(event: React.PointerEvent) {
        handlePointerDone(event, definedOnLost);
      },

      onContextMenu(event: React.SyntheticEvent) {
        if (dragContextRef.current) {
          definedOnLost && definedOnLost(dragContextRef.current);
          handlePointerFinally(event);
        }
      },
    },
    dragContextRef,
  ];
}
